import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ViewModeProvider } from "@/contexts/ViewModeContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { HelpCenterButton } from "@/components/support/HelpCenterButton";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { NotificationPrompt } from "@/components/pwa/NotificationPrompt";

import { EmailNurturingManager } from "@/components/EmailNurturingManager";
import { VoiceInterfaceBoundary } from "@/components/voice/VoiceInterfaceBoundary";
import { FeatureAdoptionTracker } from "@/components/analytics/FeatureAdoptionTracker";
import { GA4GlobalTracker } from "@/components/analytics/GA4GlobalTracker";
import { AccessibilityProvider } from "@/components/accessibility/AccessibilityProvider";
import { AccessibilityPanel } from "@/components/accessibility/AccessibilityPanel";
import { InteractiveOnboarding } from "@/components/onboarding/InteractiveOnboarding";

// Lazy load pages for code splitting
const Index = lazy(() => import("@/pages/Index"));
const Auth = lazy(() => import("@/pages/Auth"));
const Entrada = lazy(() => import("@/pages/Entrada"));
const PainelExecutivo = lazy(() => import("@/pages/PainelExecutivo"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Planos = lazy(() => import("@/pages/Planos"));
const Diagnostico = lazy(() => import("@/pages/Diagnostico"));
const DiagnosticoRapido = lazy(() => import("@/pages/DiagnosticoRapido"));
const Relatorio = lazy(() => import("@/pages/Relatorio"));
const ParaContadores = lazy(() => import("@/pages/ParaContadores"));
const ParceriaContadores = lazy(() => import("@/pages/ParceriaContadores"));
const Contadores = lazy(() => import("@/pages/Contadores"));
const ContadorCadastro = lazy(() => import("@/pages/ContadorCadastro"));
const ContadorLogin = lazy(() => import("@/pages/ContadorLogin"));
const ContadorDashboard = lazy(() => import("@/pages/ContadorDashboard"));
const ContadorSucesso = lazy(() => import("@/pages/ContadorSucesso"));
const BoasVindasIndicado = lazy(() => import("@/pages/BoasVindasIndicado"));
const RelatorioPublico = lazy(() => import("@/pages/RelatorioPublico"));
const TermoParceria = lazy(() => import("@/pages/TermoParceria"));
const ComoFunciona = lazy(() => import("@/pages/ComoFunciona"));
const Cardapio = lazy(() => import("@/pages/Cardapio"));
const Inteligencia = lazy(() => import("@/pages/Inteligencia"));
const Ajuda = lazy(() => import("@/pages/Ajuda"));
const AdminSuporteInterno = lazy(() => import("@/pages/AdminSuporteInterno"));
const AdminMetricas = lazy(() => import("@/pages/AdminMetricas"));
const Instalar = lazy(() => import("@/pages/Instalar"));
const Integracoes = lazy(() => import("@/pages/Integracoes"));
const Changelog = lazy(() => import("@/pages/Changelog"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const TermosDeUso = lazy(() => import("@/pages/TermosDeUso"));
const RelatoriosExecutivos = lazy(() => import("@/pages/RelatoriosExecutivos"));
const RadarSentinela = lazy(() => import("@/pages/RadarSentinela"));
const RadarPublico = lazy(() => import("@/pages/RadarPublico"));
const RadarInstitucional = lazy(() => import("@/pages/RadarInstitucional"));
const RadarPage = lazy(() => import("@/pages/Radar"));
const ControleCaixaRestaurante = lazy(() => import("@/pages/ControleCaixaRestaurante"));
const CmvBar = lazy(() => import("@/pages/CmvBar"));
const GestaoFinanceiraRestaurante = lazy(() => import("@/pages/GestaoFinanceiraRestaurante"));
const MargemLucroRestaurante = lazy(() => import("@/pages/MargemLucroRestaurante"));
const FluxoCaixaBar = lazy(() => import("@/pages/FluxoCaixaBar"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogArticle = lazy(() => import("@/pages/BlogArticle"));
const Cases = lazy(() => import("@/pages/Cases"));

// Google Ads Landing Pages
const DiagnosticoLanding = lazy(() => import("@/pages/DiagnosticoLanding"));
const DiagnosticoObrigado = lazy(() => import("@/pages/DiagnosticoObrigado"));
const DiagnosticoConfirmado = lazy(() => import("@/pages/DiagnosticoConfirmado"));

// App Layout and Pages
const AppLayout = lazy(() => import("@/layouts/AppLayout"));
const VisaoGeral = lazy(() => import("@/pages/app/VisaoGeral"));
const DiagnosticoApp = lazy(() => import("@/pages/app/DiagnosticoApp"));
const RadarApp = lazy(() => import("@/pages/app/RadarApp"));
const HistoricoApp = lazy(() => import("@/pages/app/HistoricoApp"));
const AlertasApp = lazy(() => import("@/pages/app/AlertasApp"));
const PlanoConta = lazy(() => import("@/pages/app/PlanoConta"));
const AjudaApp = lazy(() => import("@/pages/app/AjudaApp"));

const queryClient = new QueryClient();

// Premium loading fallback with skeleton animation
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="relative">
        <div className="h-12 w-12 rounded-full border-4 border-muted animate-spin border-t-primary" />
      </div>
      <p className="text-sm text-muted-foreground animate-pulse">Carregando...</p>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
            <ViewModeProvider>
              <AccessibilityProvider>
                {/* Skip to main content link for keyboard users */}
                <a href="#main-content" className="skip-to-main">
                  Ir para o conteúdo principal
                </a>
                
                <Toaster />
                <Sonner />
                <ScrollToTop />
                <HelpCenterButton />
                <InstallPrompt />
                <NotificationPrompt />
                <EmailNurturingManager />
                <VoiceInterfaceBoundary />
                <AccessibilityPanel />
                <InteractiveOnboarding />
              <main id="main-content">
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route
                      path="/entrada"
                      element={
                        <ProtectedRoute>
                          <Entrada />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/planos" element={<Planos />} />
                    <Route path="/como-funciona" element={<ComoFunciona />} />
                    <Route path="/instalar" element={<Instalar />} />
                    <Route path="/changelog" element={<Changelog />} />
                    <Route path="/comecar" element={<DiagnosticoRapido />} />
                    <Route path="/radar" element={<RadarPage />} />
                    <Route path="/radar-publico" element={<Navigate to="/radar" replace />} />
                    <Route path="/radar-institucional" element={<RadarInstitucional />} />
                    <Route path="/controle-caixa-restaurante" element={<ControleCaixaRestaurante />} />
                    <Route path="/cmv-bar" element={<CmvBar />} />
                    <Route path="/gestao-financeira-restaurante" element={<GestaoFinanceiraRestaurante />} />
                    <Route path="/margem-lucro-restaurante" element={<MargemLucroRestaurante />} />
                    <Route path="/fluxo-caixa-bar" element={<FluxoCaixaBar />} />
                    <Route path="/termos" element={<TermosDeUso />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogArticle />} />
                    <Route path="/cases" element={<Cases />} />
                    
                    {/* Google Ads Landing Pages - Standalone, no header/footer */}
                    <Route path="/lp/diagnostico" element={<DiagnosticoLanding />} />
                    <Route path="/lp/diagnostico/obrigado" element={<DiagnosticoObrigado />} />
                    
                    {/* Conversion confirmation page for main flow */}
                    <Route path="/diagnostico/confirmado" element={<DiagnosticoConfirmado />} />
                    
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/para-contadores" element={<ParaContadores />} />
                    <Route path="/parceria-contadores" element={<ParceriaContadores />} />
                    <Route path="/contadores" element={<Contadores />} />
                    <Route path="/contador/cadastro" element={<ContadorCadastro />} />
                    <Route path="/contador/login" element={<ContadorLogin />} />
                    <Route path="/contador/sucesso" element={<ContadorSucesso />} />
                    <Route path="/contador/dashboard" element={<ContadorDashboard />} />
                    <Route path="/contador/termo-parceria" element={<TermoParceria />} />
                    <Route path="/boas-vindas-indicado" element={<BoasVindasIndicado />} />
                    <Route path="/relatorio-publico/:id" element={<RelatorioPublico />} />
                    
                    {/* NEW APP AREA - Central do Cliente */}
                    <Route
                      path="/app"
                      element={
                        <ProtectedRoute>
                          <AppLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<VisaoGeral />} />
                      <Route path="diagnostico" element={<DiagnosticoApp />} />
                      <Route path="radar" element={<RadarApp />} />
                      <Route path="historico" element={<HistoricoApp />} />
                      <Route path="alertas" element={<AlertasApp />} />
                      <Route path="conta" element={<PlanoConta />} />
                      <Route path="ajuda" element={<AjudaApp />} />
                    </Route>
                    
                    {/* Redirect legacy routes to new /app */}
                    <Route
                      path="/painel-executivo"
                      element={<Navigate to="/app" replace />}
                    />
                    <Route
                      path="/painel"
                      element={<Navigate to="/app" replace />}
                    />
                    <Route
                      path="/dashboard"
                      element={<Navigate to="/app" replace />}
                    />
                    <Route
                      path="/radar"
                      element={<Navigate to="/app/radar" replace />}
                    />
                    <Route
                      path="/inteligencia"
                      element={<Navigate to="/app/radar" replace />}
                    />
                    
                    <Route
                      path="/diagnostico"
                      element={<Navigate to="/app/diagnostico" replace />}
                    />
                    <Route
                      path="/relatorio/:id"
                      element={
                        <ProtectedRoute>
                          <Relatorio />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/cardapio"
                      element={
                        <ProtectedRoute requireSubscription>
                          <Cardapio />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/ajuda"
                      element={<Navigate to="/app/ajuda" replace />}
                    />
                    <Route
                      path="/admin/suporte"
                      element={
                        <ProtectedRoute>
                          <AdminSuporteInterno />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/metricas"
                      element={
                        <ProtectedRoute>
                          <AdminMetricas />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/integracoes"
                      element={
                        <ProtectedRoute>
                          <Integracoes />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/relatorios-executivos"
                      element={
                        <ProtectedRoute requirePlan="ultra">
                          <RelatoriosExecutivos />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </main>
              <FeatureAdoptionTracker />
              <GA4GlobalTracker />
              </AccessibilityProvider>
            </ViewModeProvider>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
